import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { getScenarioSettings, updateScenarioSettings, getScenarioPreview } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { fmtEUR } from '@/lib/utils';
import {
  Settings, Eye, ShieldAlert, Clock, FileCheck, Globe, CheckCircle, XCircle,
  AlertTriangle, DollarSign, Loader2, ArrowRight, BarChart3
} from 'lucide-react';

export const ScenarioPage = () => {
  const { t } = useTranslation();
  const { user, isAdmin, isGroupAdmin } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || (!isAdmin && !isGroupAdmin)) { navigate('/'); return; }
    loadSettings();
  }, [user]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const groupId = user.role === 'admin' ? undefined : user.group_id;
      const res = await getScenarioSettings(groupId);
      setSettings(res.data);
      setDraft({ ...res.data });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await getScenarioPreview({
        group_id: user.role === 'admin' ? undefined : user.group_id,
        internal_window_days: draft.internal_window_days,
        escalation_days: draft.escalation_days,
        external_enabled: draft.external_enabled,
        approval_required: draft.approval_required,
        strict_documentation: draft.strict_documentation,
      });
      setPreview(res.data);
    } catch (e) { console.error(e); toast.error('Preview failed'); }
    finally { setPreviewLoading(false); }
  };

  const handleApply = async () => {
    setSaving(true);
    try {
      await updateScenarioSettings(draft);
      toast.success('Einstellungen gespeichert');
      setSettings({ ...draft });
      setPreview(null);
    } catch (e) { console.error(e); toast.error('Fehler beim Speichern der Einstellungen'); }
    finally { setSaving(false); }
  };

  const hasChanges = settings && draft && (
    settings.internal_window_days !== draft.internal_window_days ||
    settings.escalation_days !== draft.escalation_days ||
    settings.external_enabled !== draft.external_enabled ||
    settings.approval_required !== draft.approval_required ||
    settings.strict_documentation !== draft.strict_documentation
  );

  if (!user || (!isAdmin && !isGroupAdmin)) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="scenario-page">
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-manrope text-3xl font-bold flex items-center gap-3" data-testid="scenario-title">
            <BarChart3 className="h-8 w-8 text-blue-500" />
            {t('scenario.title')}
          </h1>
          <p className="text-muted-foreground mt-1">{t('scenario.subtitle')}</p>
        </div>

        {loading ? (
          <div className="space-y-4"><Skeleton className="h-64" /><Skeleton className="h-64" /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Settings Panel */}
            <Card data-testid="scenario-settings-panel">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5 text-blue-500" />
                  {t('scenario.whatIf')}
                </CardTitle>
                <CardDescription>{t('admin.scenariosDesc')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-orange-500" />
                    {t('admin.internalWindow')}
                  </Label>
                  <Input
                    data-testid="scenario-internal-window"
                    type="number"
                    value={draft?.internal_window_days || 0}
                    onChange={(e) => setDraft(d => ({ ...d, internal_window_days: parseInt(e.target.value) || 0 }))}
                  />
                  <p className="text-xs text-muted-foreground">{t('admin.internalWindowDesc')}</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    {t('admin.escalationDays')}
                  </Label>
                  <Input
                    data-testid="scenario-escalation-days"
                    type="number"
                    value={draft?.escalation_days || 0}
                    onChange={(e) => setDraft(d => ({ ...d, escalation_days: parseInt(e.target.value) || 0 }))}
                  />
                  <p className="text-xs text-muted-foreground">{t('admin.escalationDaysDesc')}</p>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-blue-500" />
                    <div>
                      <p className="text-sm font-medium">{t('admin.externalEnabled')}</p>
                      <p className="text-xs text-muted-foreground">{t('admin.externalEnabledDesc')}</p>
                    </div>
                  </div>
                  <Switch
                    data-testid="scenario-external-toggle"
                    checked={draft?.external_enabled ?? true}
                    onCheckedChange={(v) => setDraft(d => ({ ...d, external_enabled: v }))}
                  />
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <div>
                      <p className="text-sm font-medium">{t('admin.approvalRequired')}</p>
                      <p className="text-xs text-muted-foreground">{t('admin.approvalRequiredDesc')}</p>
                    </div>
                  </div>
                  <Switch
                    data-testid="scenario-approval-toggle"
                    checked={draft?.approval_required ?? true}
                    onCheckedChange={(v) => setDraft(d => ({ ...d, approval_required: v }))}
                  />
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-2">
                    <FileCheck className="h-4 w-4 text-violet-500" />
                    <div>
                      <p className="text-sm font-medium">{t('admin.strictDocs')}</p>
                      <p className="text-xs text-muted-foreground">{t('admin.strictDocsDesc')}</p>
                    </div>
                  </div>
                  <Switch
                    data-testid="scenario-strict-docs-toggle"
                    checked={draft?.strict_documentation ?? false}
                    onCheckedChange={(v) => setDraft(d => ({ ...d, strict_documentation: v }))}
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handlePreview}
                    disabled={previewLoading}
                    className="flex-1"
                    variant="outline"
                    data-testid="scenario-preview-btn"
                  >
                    {previewLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
                    {t('scenario.preview')}
                  </Button>
                  <Button
                    onClick={handleApply}
                    disabled={saving || !hasChanges}
                    className="flex-1 bg-orange-500 hover:bg-orange-600"
                    data-testid="scenario-apply-btn"
                  >
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRight className="h-4 w-4 mr-2" />}
                    {t('scenario.applyChanges')}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Preview Results Panel */}
            <Card data-testid="scenario-preview-panel">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldAlert className="h-5 w-5 text-orange-500" />
                  {t('scenario.previewResult')}
                </CardTitle>
                <CardDescription>
                  {preview ? t('scenario.whatIf') : t('scenario.noChanges')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {previewLoading ? (
                  <div className="space-y-3">
                    {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-12" />)}
                  </div>
                ) : preview ? (
                  <div className="space-y-3">
                    <ImpactRow
                      icon={<AlertTriangle className="h-4 w-4 text-red-500" />}
                      label={t('scenario.escalatedRequests')}
                      value={preview.impact.escalated_requests}
                      color="text-red-600"
                    />
                    <ImpactRow
                      icon={<Clock className="h-4 w-4 text-orange-500" />}
                      label={t('scenario.pendingRequests')}
                      value={preview.impact.pending_requests}
                      color="text-orange-600"
                    />
                    <ImpactRow
                      icon={<FileCheck className="h-4 w-4 text-violet-500" />}
                      label={t('scenario.docsBlocked')}
                      value={preview.impact.docs_missing_blocked}
                      color="text-violet-600"
                    />
                    <ImpactRow
                      icon={<Clock className="h-4 w-4 text-blue-500" />}
                      label={t('scenario.internalWindow')}
                      value={preview.impact.items_in_internal_window}
                      color="text-blue-600"
                    />
                    <ImpactRow
                      icon={<Globe className="h-4 w-4 text-green-500" />}
                      label={t('scenario.externalAccess')}
                      value={preview.impact.external_access === 'enabled' ? t('scenario.enabled') : t('scenario.blocked')}
                      color={preview.impact.external_access === 'enabled' ? 'text-green-600' : 'text-red-600'}
                      isBadge
                    />
                    <ImpactRow
                      icon={<CheckCircle className="h-4 w-4 text-emerald-500" />}
                      label={t('scenario.autoApproved')}
                      value={preview.impact.auto_approved_requests}
                      color="text-emerald-600"
                    />
                    <div className="border-t pt-3 mt-3">
                      <ImpactRow
                        icon={<DollarSign className="h-4 w-4 text-red-500" />}
                        label={t('scenario.valueAtRisk')}
                        value={`\u20AC${fmtEUR(preview.impact.value_at_risk_kEUR || 0)}`}
                        color="text-red-600"
                        bold
                      />
                      <ImpactRow
                        icon={<ShieldAlert className="h-4 w-4 text-red-500" />}
                        label={t('scenario.itemsAtRisk')}
                        value={preview.impact.items_at_risk}
                        color="text-red-600"
                        bold
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                    <Eye className="h-12 w-12 mb-3 opacity-20" />
                    <p className="text-sm text-center">{t('scenario.noChanges')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

function ImpactRow({ icon, label, value, color, isBadge, bold }) {
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg bg-muted/30 ${bold ? 'bg-muted/60' : ''}`}>
      <div className="flex items-center gap-2">
        {icon}
        <span className={`text-sm ${bold ? 'font-semibold' : ''}`}>{label}</span>
      </div>
      {isBadge ? (
        <Badge variant="outline" className={color}>{value}</Badge>
      ) : (
        <span className={`text-sm font-bold ${color}`}>{value}</span>
      )}
    </div>
  );
}
